<template>
    <div class="bg-white dark:bg-dark rounded-lg p-4 w-full">
        <div ref="chartContainer" class="w-full"></div>
        <p class="text-sm text-gray-600 dark:text-gray-400 mt-2">{{ lastUpdate }}</p>
    </div>
</template>

<script setup>
    import { ref, onMounted, onUnmounted, defineProps } from 'vue';
    import { utcToLocal } from '@/tools/TimeFunctions';
    import { collection, query, orderBy, limit, onSnapshot, getDocs} from 'firebase/firestore';
    import { db } from '@/firebase';
    import Plotly from 'plotly.js-dist';

    const props = defineProps({
        template: Object,
        dataID: String,
    });

    const chartContainer = ref(null);
    const interval = ((props.template.range[1] - props.template.range[0]) / (6));
    const lastUpdate = ref('Last Update:');
    let resizeObserver = null;
    let resizeTimeout = null;

    const createGaugeData = () => ([{
        type: "indicator",
        mode: "gauge+number",
        value: null,
        number: {
            prefix: ``,
            suffix: `<span style="font-size: 0.8em;">${props.template.unit || ''}</span> `,
            font: { 
                size: window.innerWidth < 768 ? 30 : 40,
                family: 'Inter, system-ui, sans-serif'
            },
            valueformat: props.template.valuePrecision,
        },
        title: { 
            text: props.template.title, 
            font: { 
                size: window.innerWidth < 768 ? 18 : 24,
                family: 'Inter, system-ui, sans-serif'
            } 
        },
        gauge: {
            axis: { 
                range: null,
                tickwidth: 1,
                tickcolor: "currentColor"
            },
            bar: { color: props.template.color },
            bgcolor: props.template.bgColor || 'white',
            borderwidth: 2,
            bordercolor: "gray",
            steps: []
        }
    }]);

    const createLayout = () => {
        if (!chartContainer.value) return null;
        
        const containerWidth = chartContainer.value.offsetWidth;
        if (!containerWidth) return null;

        const isMobile = window.innerWidth < 768;
        return {
            width: containerWidth,
            height: isMobile ? 180 : 200,
            margin: { t: 50, r: 25, l: 25, b: 25 },
            paper_bgcolor: "transparent",
            font: { 
                color: "currentColor",
                family: 'Inter, system-ui, sans-serif'
            }
        };
    };

    const config = {
        displaylogo: false,
        responsive: true,
        staticPlot: true
    };

    const updateChart = () => {
        if (!chartContainer.value) return;
        
        const layout = createLayout();
        if (!layout) {
            // If layout is null, try again in the next frame when dimensions might be available
            requestAnimationFrame(updateChart);
            return;
        }

        const data = createGaugeData();

        // Set Prefix
        if (props.template.prefix) {
            data[0].number.prefix = `<span style="font-size: 0.6em;">${props.template.prefix}</span><br>`;
        }

        // Set the range
        if (Array.isArray(props.template.range)) {
            data[0].gauge.axis.range = props.template.range;
        }

        // Set the steps
        if (props.template.steps) {
            data[0].gauge.steps = props.template.steps.map(step => ({
                range: Array.isArray(step.range) ? step.range : [null, null],
                color: step.color,
                thickness: `${step.thickness || 1}`
            }));
        }

        // Set tick Intervals
        const tickvals = Array.from({ length: 7 }, (_, i) => {
            const value = props.template.range[0] + interval * i;
            return interval < 1 ? parseFloat(value.toFixed(2)) : Math.round(value);
        });
        data[0].gauge.axis.tickvals = tickvals;

        // Set delta
        if (props.template.delta) {
            data[0].mode = "gauge+number+delta";
            data[0].delta = props.template.delta;
        }

        // Set threshold
        if (props.template.threshold) {
            data[0].gauge.threshold = {
                line: { color: "currentColor", width: 4 },
                thickness: 1,
                value: props.template.threshold
            };
        }

        try {
            Plotly.newPlot(chartContainer.value, data, layout, config);
        } catch (error) {
            console.error('Error plotting gauge:', error);
            // Try again in the next frame
            requestAnimationFrame(updateChart);
        }
    };

    const updateValue = (docData) => {
        if (!chartContainer.value) return;

        let value;
        if (props.template.field) {
            value = parseFloat(docData[props.template.field]).toFixed(2);
        } else if (props.template.calculate) {
            const { formula, denominator } = props.template.calculate;
            const variables = {};
            denominator.forEach((field) => {
                variables[field] = parseFloat(docData[field]);
            });
            const calculateFn = new Function(...denominator, `return ${formula}`);
            value = calculateFn(...Object.values(variables)).toFixed(2);
        }

        if (!isNaN(value)) {
            try {
                Plotly.restyle(chartContainer.value, { 'value': [value] });
            } catch (error) {
                console.error('Error updating gauge value:', error);
            }
        }
    };

    const getLastUpdate = async(subcollection) => {
        const subcollectionRef = collection(db, 'data', props.dataID, subcollection);
        const q = query(subcollectionRef, orderBy("messageUTC", "desc"), limit(1));

        const querySnapshot = await getDocs(q);
        querySnapshot.forEach((doc) => {
            const docData = doc.data();
            lastUpdate.value = `Last Update: ${utcToLocal(docData.messageUTC, '12hour')}`;
            updateValue(docData);
        });
    };

    const setSnapshots = (subcollection) => {
        const subcollectionRef = collection(db, 'data', props.dataID, subcollection);
        const q = query(subcollectionRef, orderBy('messageUTC', 'desc'), limit(1));
        onSnapshot(q, (snapshot) => {
            snapshot.docChanges().forEach((change) => {
                const docData = change.doc.data();
                lastUpdate.value = `Last Update: ${utcToLocal(docData.messageUTC, '12hour')}`;
                updateValue(docData);
            });
        });
    };

    // Debounced version of updateChart
    const debouncedUpdateChart = () => {
        if (resizeTimeout) {
            clearTimeout(resizeTimeout);
        }
        resizeTimeout = setTimeout(() => {
            if (chartContainer.value) {
                const layout = createLayout();
                if (layout) {
                    try {
                        Plotly.relayout(chartContainer.value, layout);
                    } catch (error) {
                        console.error('Error updating layout:', error);
                        // If relayout fails, try a full redraw
                        updateChart();
                    }
                }
            }
        }, 250); // Wait for 250ms after the last resize event
    };

    onMounted(() => {
        // Get last update
        getLastUpdate(props.template.source);

        // Set snapshots
        setSnapshots(props.template.source);

        // Set up resize observer with debounced handler
        resizeObserver = new ResizeObserver(() => {
            debouncedUpdateChart();
        });
        
        if (chartContainer.value) {
            resizeObserver.observe(chartContainer.value);
        }

        // Initial chart render
        updateChart();
    });

    onUnmounted(() => {
        if (resizeTimeout) {
            clearTimeout(resizeTimeout);
        }
        if (resizeObserver && chartContainer.value) {
            resizeObserver.unobserve(chartContainer.value);
            resizeObserver.disconnect();
        }
        if (chartContainer.value) {
            Plotly.purge(chartContainer.value);
        }
    });
</script>
